<template>
    <div
        :class="{ 'opacity-0': !isRendererReady }"
        class="transition-opacity duration-300">
        <div
            ref="wrap"
            class="three relative transform-gpu"></div>

        <ClientOnly>
            <Teleport to="#teleports">
                <ModelTooltip
                    :is-hover-model
                    :tab
                    :top="tooltipPos.y"
                    :left="tooltipPos.x" />
            </Teleport>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { useRafFn, useWindowSize } from '@vueuse/core'
import gsap from 'gsap'
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'
import { PMREMGenerator } from 'three/src/extras/PMREMGenerator.js'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import type { TabInfo } from '~/components/particles/first-screen/tab.types'
import ModelTooltip from '~/components/particles/model-tooltip/ModelTooltip.vue'

/* ------------------------------------------------------------------ constants */
const OBJECTS = {
    'radioguides-tab': {
        pos: [0, 0, 0] as THREE.Vector3Tuple,
        path: '/assets/3d/radioguide/radiogid-4.glb',
        rotate: 0,
        camPos: [-1.5, 0, 0] as THREE.Vector3Tuple,
        lookAt: [0, 0, 0] as THREE.Vector3Tuple,
    },
    'cabs-tab': {
        pos: [0, 0, 0] as THREE.Vector3Tuple,
        path: '/assets/3d/cabina/Cabina.glb',
        rotate: 0,
        camPos: [-4, 2, -4] as THREE.Vector3Tuple,
        lookAt: [0, 1, 0] as THREE.Vector3Tuple,
    },
} as const

type ModelKey = keyof typeof OBJECTS

const HDR_PATH = '/assets/3d/hdr-1.hdr'

// props
const props = defineProps<{
    controlType?: 'mouse' | 'button'
    model: ModelKey
    tab: TabInfo
}>()
const emit = defineEmits<{
    ready: []
}>()
const controlType = props.controlType ?? 'mouse'
const router = useRouter()

// refs (DOM)
const wrap = ref<HTMLDivElement | null>(null)
const isRendererReady = ref(false)

// Three.js core objects (создаём на клиенте, чтобы не падать на SSR)
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer | null = null
let controls: OrbitControls | null = null
let modelObject: THREE.Object3D | null = null
let isUnmounted = false

/* ------------------------------------------------------------------ helper */
function createCore() {
    scene = new THREE.Scene()
    camera = new THREE.PerspectiveCamera(
        50,
        width.value / height.value,
        0.1,
        1000
    )
}

async function loadModel() {
    const { path, pos, rotate } = OBJECTS[props.model]
    const gltf = await new GLTFLoader().loadAsync(path)
    if (isUnmounted) return
    modelObject = gltf.scene
    modelObject.position.set(...pos)
    modelObject.rotation.y = rotate
    scene.add(modelObject)
}

async function initEnv() {
    if (props.model !== 'cabs-tab') return
    const hdr = await new RGBELoader().loadAsync(HDR_PATH)
    if (isUnmounted) {
        hdr.dispose()
        return
    }
    hdr.mapping = THREE.CubeUVReflectionMapping
    const pmrem = new PMREMGenerator(renderer!)
    scene.environment = pmrem.fromEquirectangular(hdr).texture
    hdr.dispose()
}

function initLights() {
    const ambient = new THREE.AmbientLight(0x404040, 45)
    scene.add(ambient)

    const { pos } = OBJECTS[props.model]
    const dir = new THREE.DirectionalLight(0xffffff, 1)
    dir.position.set(pos[0] - 1.5, pos[1] + 1, pos[2])
    scene.add(dir)
}

function setupRenderer() {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setClearColor(0x000000, 0)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.NeutralToneMapping
    renderer.toneMappingExposure = 1
    renderer.physicallyCorrectLights = true
    renderer.domElement.addEventListener('mousemove', handleCanvasMouseMove)
    renderer.domElement.addEventListener('mouseleave', handleCanvasMouseLeave)
    renderer.domElement.addEventListener('click', handleCanvasClick)
    wrap.value?.appendChild(renderer.domElement)
}

function setupControls() {
    if (!renderer) return
    controls = new OrbitControls(camera, renderer.domElement)
    controls.enablePan = false

    controls.enableZoom = false

    controls.dampingFactor = 0.08
    const { camPos, lookAt } = OBJECTS[props.model]
    camera.position.set(...camPos)
    controls.target.set(...lookAt)
    controls.update()

    updateCameraAdaptive()
}

/* ------------------------------------------------------------------ mouse tilt */
const mouse = { x: 0, y: 0 }

function applyMouseTilt() {
    if (!modelObject) return
    const { innerWidth: w, innerHeight: h } = window
    const maxTilt = 0.2
    const maxRotY = 1
    const tiltX = ((mouse.y - h / 2) / (h / 2)) * maxTilt
    const tiltZ = (-(mouse.x - w / 2) / (w / 2)) * maxTilt
    const rotY = ((mouse.x - w / 2) / (w / 2)) * maxRotY
    gsap.to(modelObject.rotation, {
        x: tiltX,
        y: rotY,
        z: tiltZ,
        duration: 1.25,
        ease: 'power2.out',
    })
}

function handleWindowMouseMove(e: MouseEvent): void {
    if (controlType !== 'mouse') return

    mouse.x = e.clientX
    mouse.y = e.clientY
}

/* ------------------------------------------------------------------ mouse on model */
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()
const tooltipPos = ref({ x: 0, y: 0 })

const isHoverModel = ref(false)
const hasMouseMoved = ref(false)
let isMouseOverCanvas = false

function updatePointer(e: MouseEvent): void {
    if (!renderer) return

    const rect = renderer.domElement.getBoundingClientRect()
    pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
}

function handleCanvasMouseMove(e: MouseEvent): void {
    hasMouseMoved.value = true
    tooltipPos.value.x = e.clientX
    tooltipPos.value.y = e.clientY
    isMouseOverCanvas = true

    if (controlType === 'mouse' && modelObject) {
        mouse.x = e.clientX
        mouse.y = e.clientY
    }

    updatePointer(e)
}

function handleCanvasMouseLeave(): void {
    isMouseOverCanvas = false
    resetHover()
}

function handleCanvasClick(e: MouseEvent): void {
    if (!props.tab.to || !renderer || !modelObject) return

    updatePointer(e)
    raycaster.setFromCamera(pointer, camera)

    if (raycaster.intersectObject(modelObject, true).length) {
        router.push(props.tab.to)
    }
}

function checkIntersects() {
    if (!modelObject || !hasMouseMoved.value || !isMouseOverCanvas) {
        isHoverModel.value = false
        if (renderer) renderer.domElement.style.cursor = ''
        return
    }

    raycaster.setFromCamera(pointer, camera)
    const intersects = raycaster.intersectObject(modelObject, true)

    isHoverModel.value = intersects.length > 0
    if (renderer) {
        renderer.domElement.style.cursor =
            isHoverModel.value && props.tab.to ? 'pointer' : ''
    }
}

function resetHover() {
    isHoverModel.value = false
    if (renderer) renderer.domElement.style.cursor = ''
    tooltipPos.value.x = -9999
    tooltipPos.value.y = -9999
}

watch(() => props.model, resetHover)

/* ------------------------------------------------------------------ resize */
const { width, height } = useWindowSize()

watch([width, height], () => {
    if (!renderer) return
    camera.aspect = width.value / height.value
    camera.updateProjectionMatrix()
    renderer.setSize(width.value, height.value)

    updateCameraAdaptive()
})

function updateCameraAdaptive() {
    if (!camera) return
    const { camPos } = OBJECTS[props.model]

    const breakpoint = 1024
    const sensitivity = 0.25

    let factor = 1

    if (width.value > breakpoint) {
        const ratio = width.value / breakpoint
        factor = 1 + (ratio - 1) * sensitivity
    }

    camera.position.set(
        camPos[0] * factor,
        camPos[1] * factor,
        camPos[2] * factor
    )

    if (controls) {
        controls.update()
    }
}

/* ------------------------------------------------------------------ raf */
let stopRaf: () => void = () => {}

function startLoop() {
    const raf = useRafFn(() => {
        controls?.update()
        applyMouseTilt()
        checkIntersects()
        renderer?.render(scene, camera)
    })
    stopRaf = raf.pause
}

/* ------------------------------------------------------------------ lifecycle */
onMounted(async () => {
    if (typeof window === 'undefined') return // SSR safety

    createCore()
    window.addEventListener('mousemove', handleWindowMouseMove)
    setupRenderer()
    setupControls()
    initLights()
    await Promise.all([initEnv(), loadModel()])
    if (isUnmounted) return
    await nextTick()
    if (isUnmounted || !renderer) return
    // ensure canvas size matches after DOM ready
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.render(scene, camera)
    isRendererReady.value = true
    await nextTick()
    if (isUnmounted) return
    emit('ready')
    startLoop()
})

onUnmounted(() => {
    isUnmounted = true
    stopRaf()
    window.removeEventListener('mousemove', handleWindowMouseMove)
    renderer?.domElement.removeEventListener('mousemove', handleCanvasMouseMove)
    renderer?.domElement.removeEventListener(
        'mouseleave',
        handleCanvasMouseLeave
    )
    renderer?.domElement.removeEventListener('click', handleCanvasClick)
    renderer?.dispose()
    controls?.dispose()
    if (wrap.value) wrap.value.innerHTML = ''
    if (!scene) return

    scene.traverse((obj) => {
        obj.dispose?.()
    })
})
</script>

<style scoped>
.three {
    width: 100%;
    height: 100vh;
}

.three canvas {
    pointer-events: auto;
}
</style>
